import {
  APIConstraintEnforcementMode,
  APIModelAttributes,
  APIOrgTripleAttributes,
  APIWorkspaceAttributes,
  AssetFolder,
  MetaModelTriple,
} from '@ardoq/api-types';
import { PermissionContext } from '@ardoq/access-control';
import { isArdoqError } from '@ardoq/common-helpers';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { StreamCollection } from 'streams/utils/streamUtils';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { persistentWorkspaces$ } from 'streams/workspaces/workspaces$';
import { models$ } from 'streams/models/models$';
import { assetFolders$ } from 'streams/assetFolders/assetFolders$';
import { orgTriples$ } from 'streams/orgTriples$';
import { constraintsEnforcementMode$ } from 'streams/constraintsEnforcementMode$';
import { loadMetaModel$ } from 'architectureModel/loadMetaModel';

export type ReferenceCreationDependencies = {
  permissionContext: PermissionContext;
  workspaces: StreamCollection<APIWorkspaceAttributes>;
  models: StreamCollection<APIModelAttributes>;
  assetFolders: AssetFolder[];
  orgTriples: APIOrgTripleAttributes[];
  inferredTriples: MetaModelTriple[];
  constraintsEnforcementMode: APIConstraintEnforcementMode;
};

/**
 * The triples the metamodel infers from the data that exists. They only widen
 * what is offerable in organizations that enforce no constraints, but that is
 * the default, so an empty start value degrades to the org triples alone rather
 * than to nothing.
 */
const inferredTriples$: Observable<MetaModelTriple[]> = loadMetaModel$.pipe(
  map(metaModel => (isArdoqError(metaModel) ? [] : metaModel.triples)),
  startWith([] as MetaModelTriple[])
);

/**
 * Not the side panel's `dependencies$`, for two reasons that outlived the one
 * this was written for. It is a bare `combineLatest`, so a second subscriber
 * repeats every request behind it; and it reads the metamodel through
 * `metaModel$`, whose error path opens a modal per subscriber, so a failed load
 * would greet the user twice. `loadMetaModel$` is held for the app lifetime and
 * replays, so reading it directly costs no request.
 */
export const referenceCreationDependencies$: Observable<ReferenceCreationDependencies> =
  combineLatest({
    permissionContext: currentUserPermissionContext$,
    workspaces: persistentWorkspaces$,
    models: models$,
    assetFolders: assetFolders$,
    orgTriples: orgTriples$.pipe(
      map(orgTriples => (isArdoqError(orgTriples) ? [] : orgTriples))
    ),
    inferredTriples: inferredTriples$,
    constraintsEnforcementMode: constraintsEnforcementMode$,
  });
